// App unificado — entrypoint único pra TODOS os painéis.
//
// O Shell decide qual painel renderizar baseado no URL prefix
// (/newauth, /newstudent, ...) e mantém o PersistentPlayer vivo
// entre trocas de rota dentro do mesmo painel.
//
// Cross-panel hoje ainda passa por reload (location.href = "/auth/")
// — o Shell detecta a nova URL e monta o painel correto. O player só
// "morre" no reload cross-panel; dentro de um painel ele persiste.

import { render } from "preact";
import "@app/ui/styles.css";
import {
  PersistentPlayer,
  CookieBanner,
  LegalGate,
  LegalDocModal,
  ErrorBoundary,
  installErrorReporter
} from "@app/ui";
import { initTheme } from "@app/ui/lib/theme";
import { loadBranding } from "@app/ui/lib/branding";
import { Shell } from "./Shell";

initTheme();
// Monitoramento de erros: captura window.onerror + unhandledrejection do front
// e envia pro /api/client-errors. [[project-error-monitoring]]
installErrorReporter();
// PersistentPlayer começa desligado; Shell habilita conforme o painel ativo
// (off em /auth e /pay, on nos demais). Evita flash na tela de login.
// Carrega branding (nome plataforma, favicon, cores) sem bloquear render.
// Aplica no DOM assim que chegar — title/favicon atualizam in-place.
void loadBranding();

// Janelas pop-out isoladas NUNCA têm player de música — janela dedicada e
// focada; o player no rodapé polui e dá pra controlar pela aba principal.
// Detectado pela query string aqui (antes do render) pra cortar pela raiz,
// sem depender do useEffect do Shell rodar depois.
// [[project-notes-system-caderno]] [[project-tutor-ia-tira-duvida]]
const isIsolatedPopup = (() => {
  try {
    const view = new URL(window.location.href).searchParams.get("view");
    return (
      view === "note-window" ||
      view === "notes-window" ||
      view === "recorder-window" ||
      view === "quickstudy-window"
    );
  } catch {
    return false;
  }
})();

const root = document.getElementById("app");
if (!root) throw new Error("#app not found");

// Fallback se o painel inteiro estourar no render — evita tela branca total.
const shellFallback = (
  <div class="flex min-h-screen flex-col items-center justify-center gap-3 p-6 text-center">
    <p class="text-sm font-semibold text-foreground">
      Algo deu errado ao carregar a tela.
    </p>
    <p class="text-xs text-muted-foreground">
      Já registramos o problema. Tente recarregar a página.
    </p>
    <button
      type="button"
      onClick={() => location.reload()}
      class="rounded-md bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700"
    >
      Recarregar
    </button>
  </div>
);

render(
  <>
    {/* Cada subtree num boundary próprio: um erro no legal não derruba o Shell,
        e vice-versa. [[project-error-monitoring]] */}
    <ErrorBoundary label="shell" fallback={shellFallback}>
      <Shell />
    </ErrorBoundary>
    {!isIsolatedPopup && (
      <ErrorBoundary label="player">
        <PersistentPlayer />
      </ErrorBoundary>
    )}
    {/* Legal/Consentimento (LGPD): banner de cookies + gate de aceite forçado.
        Fora de pop-outs isolados — a janela principal cuida disso.
        [[project-legal-consent-system]] */}
    {!isIsolatedPopup && (
      <ErrorBoundary label="legal">
        <CookieBanner />
        <LegalGate />
        <LegalDocModal />
      </ErrorBoundary>
    )}
  </>,
  root
);
